/**
 * SiteFooter.tsx  -  Shared marketing/legal footer.
 * Used on the landing page and every public legal page.
 */

import { Link } from 'react-router-dom'
import LitlingsMark from '@/components/LitlingsMark'

const PRODUCT_LINKS = [
  { label: 'Features',     to: '/#features' },
  { label: 'Pricing',      to: '/#pricing' },
  { label: 'Sign up free', to: '/signup' },
  { label: 'Log in',       to: '/login' },
]

const COMPANY_LINKS = [
  { label: 'Our Mission',      to: '/#mission' },
  { label: 'Contact Us',       href: 'mailto:hello@litlings.com' },
  { label: 'Enterprise Sales', href: 'mailto:hello@litlings.com?subject=Enterprise' },
  { label: 'Report a concern', href: 'mailto:safeguarding@litlings.com?subject=Safeguarding%20concern' },
]

const LEGAL_LINKS = [
  { label: 'Privacy Policy',           to: '/privacy' },
  { label: 'Terms of Service',         to: '/terms' },
  { label: 'Cookie Policy',            to: '/cookies' },
  { label: 'Safeguarding',             to: '/safeguarding' },
  { label: 'Acceptable Use',           to: '/acceptable-use' },
  { label: 'Security',                 to: '/security' },
  { label: 'Data Processing (DPA)',    to: '/dpa' },
]

function FooterLink({ label, to, href }: { label: string; to?: string; href?: string }) {
  const cls = 'hover:text-white transition-colors'
  if (href) return <a href={href} className={cls}>{label}</a>
  if (to?.startsWith('/#')) return <a href={to} className={cls}>{label}</a>
  return <Link to={to ?? '#'} className={cls}>{label}</Link>
}

export default function SiteFooter() {
  return (
    <footer className="bg-brand-ink py-14 text-white/70">
      <div className="mx-auto max-w-6xl px-5">
        <div className="mb-12 grid grid-cols-2 gap-8 md:grid-cols-4">
          <div className="col-span-2 md:col-span-1">
            <div className="mb-3 flex items-center gap-2.5">
              <LitlingsMark size={28} />
              <span className="font-display text-lg font-extrabold text-white">Litlings</span>
            </div>
            <p className="text-sm leading-relaxed">
              Putting great learning in the hands of every child, starting with reading.
            </p>
          </div>
          {[
            ['Product', PRODUCT_LINKS],
            ['Company', COMPANY_LINKS],
            ['Legal', LEGAL_LINKS],
          ].map(([title, links]) => (
            <div key={title as string}>
              <p className="mb-3 text-xs font-bold uppercase tracking-[0.14em] text-brand-yellow">{title as string}</p>
              <ul className="space-y-2 text-sm">
                {(links as { label: string; to?: string; href?: string }[]).map((l) => (
                  <li key={l.label}><FooterLink {...l} /></li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-6 text-xs md:flex-row">
          <p>&copy; 2026 Litlings. Helping children learn worldwide.</p>
          <a href="mailto:privacy@litlings.com" className="underline hover:text-white">privacy@litlings.com</a>
        </div>
      </div>
    </footer>
  )
}
